import { t } from "i18next";
import { Fragment } from "react";
import { ComputeFieldComponent } from "../computeFields";
import { Collector } from "../types";
import { CollectorNameForOverviewCF } from "./collector";

export type SculptureEditionHistoryItem = {
  description: string;
  villePays?: string | null;
  dates?: string | null;
  collectionParticuliere: boolean;
};

export const SculptureEditionHistoryCF: ComputeFieldComponent<{
  sculptureEditionHistory: SculptureEditionHistoryItem[];
}> = ({ input, language, translation, lineBreak = true }) => {
  if (!input.sculptureEditionHistory || input.sculptureEditionHistory.length === 0) {
    return null;
  }

  const historyItems = input.sculptureEditionHistory.map((history, index) => {
    const location = history.collectionParticuliere
      ? translation
        ? translation("computeField.collectionParticuliere")
        : t("computeField.collectionParticuliere", { lng: language })
      : history.villePays?.trim() || "";
    const parts = [history.description, location].filter(Boolean);
    const content = parts.join(", ");

    return (
      <Fragment key={index}>
        {content}
        {index < input.sculptureEditionHistory.length - 1 && lineBreak && <br />}
      </Fragment>
    );
  });

  return <>{historyItems}</>;
};

export const SculptureEditionSummaryCF: ComputeFieldComponent<{
  sculptureEditionNumeroEnregistrement?: string;
  sculptureEditionTypeEdition?: string;
  sculptureEditionNumerotation?: Record<string, string> | null;
  sculptureEditionDateRealisation?: string;
  collector?: Pick<
    Collector,
    | "collectionneursDescriptionNom"
    | "collectionneursDescriptionPrenom"
    | "collectionneursDescriptionCollectionPublique"
    | "collectionneursDescriptionVille"
    | "collectionneursDescriptionVillePublished"
    | "collectionneursDescriptionPays"
    | "collectionneursDescriptionPaysPublished"
  > | null;
}> = ({ input, language, translation, lineBreak = false }) => {
  const parts: (string | React.ReactNode)[] = [];

  if (input.sculptureEditionNumeroEnregistrement) {
    parts.push(input.sculptureEditionNumeroEnregistrement);
  }

  if (input.sculptureEditionTypeEdition) {
    parts.push(input.sculptureEditionTypeEdition);
  }

  if (input.sculptureEditionNumerotation) {
    const numerotation =
      input.sculptureEditionNumerotation[language] ||
      Object.values(input.sculptureEditionNumerotation)[0];
    if (numerotation) parts.push(numerotation);
  }

  if (input.sculptureEditionDateRealisation) {
    parts.push(`(${input.sculptureEditionDateRealisation})`);
  }

  if (input.collector?.collectionneursDescriptionNom) {
    parts.push(
      <CollectorNameForOverviewCF
        key="collector"
        input={{
          collectionneursDescriptionNom:
            input.collector.collectionneursDescriptionNom ?? "",
          collectionneursDescriptionPrenom:
            input.collector.collectionneursDescriptionPrenom ?? "",
          collectionneursDescriptionCollectionPublique:
            input.collector.collectionneursDescriptionCollectionPublique ?? false,
          collectionneursDescriptionVille:
            input.collector.collectionneursDescriptionVille ?? "",
          collectionneursDescriptionVillePublished:
            input.collector.collectionneursDescriptionVillePublished,
          collectionneursDescriptionPays:
            input.collector.collectionneursDescriptionPays ?? "",
          collectionneursDescriptionPaysPublished:
            input.collector.collectionneursDescriptionPaysPublished,
          showCity: true,
        }}
        language={language}
        translation={translation}
      />,
    );
  }

  if (parts.length === 0) return null;

  return (
    <>
      {parts.map((part, idx) => (
        <Fragment key={idx}>
          {part}
          {idx < parts.length - 1 && (lineBreak ? <br /> : ", ")}
        </Fragment>
      ))}
    </>
  );
};
